import { useState, type ReactNode } from 'react'

import {
  EVENT_LEDGERS,
  BATCH_EVENT_PAGE_SIZE,
  INITIAL_EVENT_OFFSETS,
  mergeHistoryDesc,
  useBatchEventPage,
  type BatchEventOffsets,
} from '@ctcells/api'
import type { HistoryEntry } from '@ctcells/contract'
import { HISTORY } from '@ctcells/fixtures'
import { errorMessage, t } from '@ctcells/i18n'

import { DataTable } from '../../atoms/DataTable.tsx'
import { Pagination } from '../../atoms/Pagination.tsx'
import { eventColumns, eventRowKey, isEmphasisEvent } from './eventColumns.tsx'

import './batchEventLog.css'

export function BatchEventLog({
  batchId,
  live,
  actions,
}: {
  batchId: string
  live: boolean
  actions?: ReactNode
}) {
  return live ? (
    <LiveBatchEventLog batchId={batchId} key={batchId} actions={actions} />
  ) : (
    <DemoBatchEventLog batchId={batchId} key={batchId} actions={actions} />
  )
}

const firstPage = () => ({
  to: new Date().toISOString(),
  page: 1,
  offsets: [INITIAL_EVENT_OFFSETS] as BatchEventOffsets[],
})

function LiveBatchEventLog({ batchId, actions }: { batchId: string; actions?: ReactNode }) {
  const [view, setView] = useState(firstPage)
  const query = useBatchEventPage({
    batchId,
    to: view.to,
    offsets: view.offsets[view.page - 1] ?? INITIAL_EVENT_OFFSETS,
  })
  const ready = query.isSuccess && !query.isFetching
  return (
    <EventLogCard
      rows={query.isError ? [] : (query.data?.items ?? [])}
      loading={query.isPending || query.isFetching}
      notice={query.isError ? errorMessage(query.error) : undefined}
      actions={actions}
      pagination={
        ready ? (
          <Pagination
            label={t('card.batchDetail.events')}
            page={view.page}
            pageCount={Math.max(1, Math.ceil(query.data.total / BATCH_EVENT_PAGE_SIZE))}
            onChange={(page) =>
              setView((previous) => ({
                ...previous,
                page,
                offsets:
                  page > previous.page
                    ? [...previous.offsets.slice(0, previous.page), query.data.nextOffsets]
                    : previous.offsets,
              }))
            }
          />
        ) : null
      }
    />
  )
}

function DemoBatchEventLog({ batchId, actions }: { batchId: string; actions?: ReactNode }) {
  const [page, setPage] = useState(1)
  const rows = mergeHistoryDesc([
    HISTORY.filter(
      (row) =>
        row.target === `batch:${batchId}` &&
        EVENT_LEDGERS.some((ledger) => ledger === row.event_type),
    ),
  ])
  return (
    <EventLogCard
      actions={actions}
      rows={rows.slice((page - 1) * BATCH_EVENT_PAGE_SIZE, page * BATCH_EVENT_PAGE_SIZE)}
      pagination={
        <Pagination
          label={t('card.batchDetail.events')}
          page={page}
          pageCount={Math.max(1, Math.ceil(rows.length / BATCH_EVENT_PAGE_SIZE))}
          onChange={setPage}
        />
      }
    />
  )
}

function EventLogCard({
  rows,
  loading = false,
  notice,
  pagination,
  actions,
}: {
  rows: readonly HistoryEntry[]
  loading?: boolean
  notice?: string | undefined
  pagination: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="card">
      <div className="card__title btnrow batch-event-log__header">
        {t('card.batchDetail.events')}
        {actions}
      </div>
      <DataTable
        caption={t('card.batchDetail.events')}
        columns={eventColumns()}
        rows={rows}
        rowKey={(row) => `${eventRowKey(row)}-${String(rows.indexOf(row))}`}
        emphasis={isEmphasisEvent}
        loading={loading}
        notice={notice}
        noticeTone="failed"
        emptyLabel={t('state.empty.events')}
      />
      {pagination}
    </div>
  )
}
